import React, { useMemo } from 'react';
import './row.scss';
import { MovementsDailies } from "../StructuralComponents";
import {
  UsersCarParkBooking,
  UsersWhereaboutsDay,
  WhereaboutsGroupType
} from '@features/whereabouts/models/movements.models';
import moment, { Moment } from "moment";
import { useSelector } from "react-redux";
import { selectAllWhereaboutsOptions } from '@features/whereabouts/store/whereaboutsOptions.duck';
import { selectIconPack } from '@shared/store/auth.duck';
import { getListOfDaysBetweenInclusiveToExclusive, isWeekend } from '@shared/utils/DateUtils';
import { findWhereaboutsOptionById, getWhereaboutsDayWithFallback } from '@shared/utils/WhereaboutsHelper';
import { MovementsDay } from "../movements-day/MovementsDay";
import { WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { IconPack } from '@shared/models/company.models';
import { canEditPastWhereabouts } from '@shared/utils/AccessControl';
import { User } from '@shared/models/user.models';

interface Props {
  areUsersMovements: boolean;
  isEditing: boolean;
  userId: string;
  sevenDayWeekEnabled?: boolean;
  carParking: UsersCarParkBooking[];
  dateFrom: string;
  dateTo: string;
  style?: any;
  movements?: UsersWhereaboutsDay[];
  enableSetMovements?: boolean;
  currentUser?: User;
  birthdayDay?: number;
  birthdayMonth?: number;
  groupId?: WhereaboutsGroupType;
}

export default function MovementsWeek(props: Props) {
  const {movements, areUsersMovements, userId, isEditing, carParking, dateFrom, dateTo} = props;
  const whereaboutsOptions = useSelector(selectAllWhereaboutsOptions);
  const iconPack = useSelector(selectIconPack);

  const datesToRender = useMemo(() => {
    return getListOfDaysBetweenInclusiveToExclusive(moment(dateFrom), moment(dateTo));
  }, [dateFrom, dateTo]);

  if (!movements) {
    return null;
  }

  return (
    <MovementsDailies style={props?.style ?? {}}>
      {datesToRender.map((date, key) => (<React.Fragment key={key}>
        <MovementsDayWrapper dateToRender={date}
                             movements={movements}
                             currentUser={props.currentUser}
                             enableSetMovements={!!props?.enableSetMovements}
                             carParking={carParking}
                             iconPack={iconPack}
                             userId={userId}
                             isEditing={isEditing}
                             whereaboutsOptions={whereaboutsOptions}
                             isUsersMovement={areUsersMovements}
                             areUsersMovements={areUsersMovements}
                             birthdayDay={props.birthdayDay}
                             birthdayMonth={props.birthdayMonth}
                             includeWeekends={!!props?.sevenDayWeekEnabled}
                             groupId={props.groupId} />
      </React.Fragment>))}

    </MovementsDailies>
  )
}

interface MovementsWrapperProps {
  dateToRender: Moment;
  movements: UsersWhereaboutsDay[];
  whereaboutsOptions: WhereaboutsOption[];
  enableSetMovements: boolean;
  carParking: UsersCarParkBooking[];
  iconPack: IconPack;
  userId: string;
  isUsersMovement: boolean;
  areUsersMovements: boolean;
  isEditing: boolean;
  currentUser?: User;
  includeWeekends: boolean;
  birthdayDay?: number;
  birthdayMonth?: number;
  groupId?: WhereaboutsGroupType;
}

function MovementsDayWrapper(props: MovementsWrapperProps) {
  const {whereaboutsOptions} = props;

  const whereabouts = useMemo(() => {
    return getWhereaboutsDayWithFallback(props.dateToRender, props.userId, props.movements)
  }, [props.dateToRender, props.movements, props.userId]);

  const getOptionFor = (statusId: number) => {
    return findWhereaboutsOptionById(whereaboutsOptions, statusId);
  }

  if (!props.includeWeekends && isWeekend(props.dateToRender)) {
    return null;
  }

  return (
    <MovementsDay isEditing={props.isEditing}
                  amStatus={getOptionFor(whereabouts.amStatusId)}
                  pmStatus={getOptionFor(whereabouts.pmStatusId)}
                  dayNote={whereabouts?.dayNote}
                  amOptionItemLabel={whereabouts?.amOptionItemLabel}
                  pmOptionItemLabel={whereabouts?.pmOptionItemLabel}
                  officeAmLocationId={whereabouts.amOfficeId}
                  deskAmLabel={whereabouts.amDeskLabel}
                  officePmLocationId={whereabouts.pmOfficeId}
                  deskPmLabel={whereabouts.pmDeskLabel}
                  amDeskId={whereabouts.amDeskId}
                  pmDeskId={whereabouts.pmDeskId}
                  carParkingRequestedAm={whereabouts.amGeneralParking}
                  carParkingRequestedPm={whereabouts.pmGeneralParking}
                  momentDate={props.dateToRender}
                  isUsersMovement={props.areUsersMovements}
                  enableSetMovements={props.enableSetMovements || canEditPastWhereabouts(whereabouts, props.currentUser)}
                  carParking={props.carParking}
                  iconPack={props.iconPack}
                  userId={props.userId}
                  amCarParkSpaceId={whereabouts.amParkingSpaceId}
                  pmCarParkSpaceId={whereabouts.pmParkingSpaceId}
                  amParkingSpaceLabel={whereabouts.amParkingSpaceLabel}
                  pmParkingSpaceLabel={whereabouts.pmParkingSpaceLabel}
                  isWhereaboutsSaving={!!whereabouts.isWhereaboutsSaving}
                  whereaboutsUpdateFailed={!!whereabouts.whereaboutsUpdateFailed}
                  birthdayDay={props.birthdayDay}
                  birthdayMonth={props.birthdayMonth}
                  amApproved={whereabouts.amApproved}
                  pmApproved={whereabouts.pmApproved}
                  groupId={props.groupId} />
  )
}
